import React from 'react';
import { styled } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Box, Link, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import LocationOn from '@mui/icons-material/LocationOn';
import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';
import { OrderToTableLocationPicker } from 'components/location/OrderToTableLocationPicker';
import { OrderScenario } from 'components/order/model/Order';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { addOpacity } from 'lib/helpers';
import { MuiMap } from 'lib/MuiMap';
import { isDefined } from 'lib/typeInference';
import { LocationRouteParams } from 'pages/routes';
import { ApplicationState } from 'store/store';
import { useMerchantLogo } from 'lib/appbar/useMerchantLogo';
import { ScenarioList } from './ScenarioList';

const PREFIX = 'LandingDesktop';

const classes = {
    map: `${PREFIX}map`,
    locationIcon: `${PREFIX}locationIcon`,
    locationTitle: `${PREFIX}locationTitle`,
    phone: `${PREFIX}phone`,
    scenarioBackground: `${PREFIX}scenarioBackground`,
    landingDetails: `${PREFIX}landingDetails`
};

const StyledBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1.5),
    [`& .${classes.map}`]: {
        width: '100%',
        height: '150px',
        flexShrink: 0
    },

    [`& .${classes.locationIcon}`]: {
        marginRight: theme.spacing(0.5)
    },

    [`& .${classes.locationTitle}`]: {
        fontWeight: theme.typography.fontWeightBold
    },

    [`& .${classes.phone}`]: {
        color: theme.palette.text.primary
    },

    [`& .${classes.scenarioBackground}`]: {
        backgroundColor: addOpacity(theme.palette.background.default, 0.8),
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2)
    },

    [`& .${classes.landingDetails}`]: {
        overflow: 'hidden'
    }
}));

interface IProps {
    isLocationRetired: boolean;
    handleViewMenu: () => void;
    handleScenarioChange: (scenario: OrderScenario) => () => void;
    menuLink: string | undefined;
    showTableNumber: boolean;
    onTableChange?: ({ previous, current }: { previous?: string; current: string }) => void;
    disableTableNumberEdit?: boolean;
}

export const LandingDesktop: React.FC<React.PropsWithChildren<IProps>> = ({
    isLocationRetired,
    handleViewMenu,
    handleScenarioChange,
    menuLink,
    showTableNumber,
    onTableChange,
    disableTableNumberEdit
}) => {
    const { t } = useTranslation();

    const { locationId, merchantId } = useParams<LocationRouteParams>();
    const renderMerchantTitle = useMerchantLogo();
    const { currentLocation: location } = useLocationHelpers();

    const locationGeo = React.useMemo(() => {
        if (location) {
            return {
                lat: location?.geo[1],
                lng: location?.geo[0],
                markerName: location?.title
            };
        }
        return null;
    }, [location]);
    const distanceUnit = useSelector(
        (state: ApplicationState) => state.settings.settings?.region.distanceUnit
    );

    let distance: number | undefined = location?.distance || 0;
    if (distanceUnit === 'mi') {
        distance = isDefined(distance) ? distance * 0.621371 : undefined;
    }
    const addressLine = React.useMemo(
        () =>
            !!location
                ? [
                      location.address.address,
                      location.address.town,
                      location.address.country,
                      location.address.postcode
                  ]
                      .filter(item => !!item)
                      .join(', ')
                : '',
        [location]
    );

    return (
        <>
            <MuiAppBar title={renderMerchantTitle} disableBack />
            <StyledBox>
                <Grid container spacing={3}>
                    <Grid size={4}>
                        <OrderToTableLocationPicker
                            disableBottomSpace
                            pickerTitle="location"
                            locationId={locationId}
                            showTableNumber={showTableNumber}
                            onTableChange={onTableChange}
                            disableEdit={disableTableNumberEdit}
                        />
                        <Box marginTop={2}>
                            <Paper className={classes.landingDetails}>
                                {locationGeo && (
                                    <MuiMap
                                        width={0}
                                        center={locationGeo}
                                        options={{
                                            keyboardShortcuts: false,
                                            disableDefaultUI: true,
                                            zoomControl: true,
                                            cameraControl: false
                                        }}
                                        markers={[locationGeo]}
                                        className={classes.map}
                                        useCustomPin
                                        zoom={15}
                                    />
                                )}
                                <Box padding={2}>
                                    <div>
                                        <Typography className={classes.locationTitle} variant="h6">
                                            {location?.title}
                                        </Typography>
                                        <Typography variant="body2">{location?.address.summary}</Typography>
                                        {!!distance && (
                                            <Typography variant="caption">
                                                <LocationOn
                                                    className={classes.locationIcon}
                                                    fontSize="inherit"
                                                />
                                                {`${Math.floor(distance).toLocaleString()}${
                                                    distanceUnit || 'km'
                                                }`}
                                            </Typography>
                                        )}
                                    </div>
                                    <Box marginTop={1}>
                                        <Typography>
                                            <Trans
                                                i18nKey="LOCATION_ADDRESS"
                                                components={[<b key="address" />]}
                                                values={{ address: addressLine }}
                                            />
                                        </Typography>
                                    </Box>
                                    <Box marginTop={1}>
                                        {location?.contacts.phone && (
                                            <Link
                                                className={classes.phone}
                                                href={`tel:${location.contacts.phone}`}
                                            >
                                                <Typography>
                                                    <b>
                                                        {t('LOCATION_CONTACT_PHONE', {
                                                            phone: location.contacts.phone
                                                        })}
                                                    </b>
                                                </Typography>
                                            </Link>
                                        )}
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                    </Grid>
                    <Grid size={8}>
                        <Box className={classes.scenarioBackground}>
                            <ScenarioList
                                merchantId={merchantId}
                                currentLocation={location}
                                isLocationRetired={isLocationRetired}
                                menuLink={menuLink}
                                handleScenarioChange={handleScenarioChange}
                                handleViewMenu={handleViewMenu}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </StyledBox>
        </>
    );
};
